import { useEffect, useState, useRef } from "react";
import { Button, Form, Input } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import API from "scripts/api";
import Resources from "scripts/resources";
import Rules from "scripts/rules";
import { GetUrlParams } from "scripts/helpers";
import { Patterns } from "scripts/settings";

// import InputCode from "components/InputCode";

const InputCode = ({ countdown, onChange, onResend }) => {
	const pattern = "[0-9]{1}";
	const inputsRef = useRef([]);

	const handleResult = () => {
		const res = inputsRef.current.map((input) => input.value).join("");

		onChange(res);
	};

	const handleFocus = (e) => {
		e.target.select();
	};

	const handleChange = (e) => {
		const {
			target: { value, nextElementSibling },
		} = e;

		if (value.length > 1) {
			e.target.value = value.charAt(0);

			if (nextElementSibling !== null) nextElementSibling.focus();
		} else {
			if (value.match(pattern)) {
				if (nextElementSibling !== null) nextElementSibling.focus();
			} else {
				e.target.value = "";
			}
		}

		handleResult();
	};

	const handleKeyDown = (e) => {
		const {
			key,
			target: { value, previousElementSibling },
		} = e;

		if (key === "Backspace") {
			if (value === "") {
				if (previousElementSibling !== null) {
					const t = previousElementSibling;
					t.value = "";
					t.focus();
					e.preventDefault();
				}
			} else {
				e.target.value = "";
			}

			handleResult();
		}
	};

	const handlePaste = (e) => {
		const pastedValue = e.clipboardData.getData("Text");

		let currentInput = 0;

		for (let i = 0; i < pastedValue.length; i++) {
			const pastedCharacter = pastedValue.charAt(i);
			const currentValue = inputsRef.current[currentInput].value;

			if (pastedCharacter.match(pattern)) {
				if (!currentValue) {
					inputsRef.current[currentInput].value = pastedCharacter;

					if (inputsRef.current[currentInput].nextElementSibling !== null) {
						inputsRef.current[currentInput].nextElementSibling.focus();
						currentInput++;
					}
				}
			}
		}

		handleResult();

		e.preventDefault();
	};

	return (
		<div className="code">
			<div className="group">
				<input
					type="tel"
					onKeyDown={handleKeyDown}
					onChange={handleChange}
					onFocus={handleFocus}
					onPaste={handlePaste}
					ref={(el) => (inputsRef.current[0] = el)}
					className="ant-input ant-input-lg ant-input-rtl"
					autoComplete="off"
				/>
				<input
					type="tel"
					onKeyDown={handleKeyDown}
					onChange={handleChange}
					onFocus={handleFocus}
					onPaste={handlePaste}
					ref={(el) => (inputsRef.current[1] = el)}
					className="ant-input ant-input-lg ant-input-rtl"
					autoComplete="off"
				/>
				<input
					type="tel"
					onKeyDown={handleKeyDown}
					onChange={handleChange}
					onFocus={handleFocus}
					onPaste={handlePaste}
					ref={(el) => (inputsRef.current[2] = el)}
					className="ant-input ant-input-lg ant-input-rtl"
					autoComplete="off"
				/>
				<input
					type="tel"
					onKeyDown={handleKeyDown}
					onChange={handleChange}
					onFocus={handleFocus}
					onPaste={handlePaste}
					ref={(el) => (inputsRef.current[3] = el)}
					className="ant-input ant-input-lg ant-input-rtl"
					autoComplete="off"
				/>
				<input
					type="tel"
					onKeyDown={handleKeyDown}
					onChange={handleChange}
					onFocus={handleFocus}
					onPaste={handlePaste}
					ref={(el) => (inputsRef.current[4] = el)}
					className="ant-input ant-input-lg ant-input-rtl"
					autoComplete="off"
				/>
			</div>
			<span className="ant-btn" disabled={countdown > 0} onClick={onResend}>
				{countdown ? countdown : Resources.resend}
			</span>
		</div>
	);
};

const Component = () => {
	const initialState = {};
	const [state, setState] = useState(initialState);
	const { submitting } = state;
	const [form] = Form.useForm();
	const { search } = useLocation();
	const navigate = useNavigate();

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));

				API.auth
					.passReset(values)
					.then(() => {
						navigate({
							pathname: "/auth/login",
							search: `?mobile=${values.phone}`,
						});
					})
					.catch(() => {
						setState((prevState) => ({
							...prevState,
							submitting: false,
						}));
					});
			});
		}
	};

	const onResend = () => {
		const { mobile } = GetUrlParams(search);
		API.auth
			.getOtp({ phone: `0${mobile}` })
			.then(() => {})
			.finally(() => {});
	};

	const handleCompare = (rule, value) => {
		return !value || form.getFieldValue("password") === value
			? Promise.resolve()
			: Promise.reject(Resources.comparePass);
	};

	const componentDidMount = () => {
		const { mobile } = GetUrlParams(search);

		if (Patterns.mobile.test(`0${mobile}`)) {
			form.setFieldValue("phone", `0${mobile}`);
		} else {
			navigate("/auth/login", { replace: true });
		}
	};

	useEffect(componentDidMount, []);

	return (
		<div className="wrapper">
			<img src="/images/logo-black.png" alt="Shop" className="brand" />
			<h1 className="heading">{`${Resources.enter} ${Resources.to} ${Resources.account}`}</h1>
			<Form form={form} layout="vertical">
				<Form.Item name="phone" label={Resources.mobile} rules={Rules.mobile}>
					<Input type="tel" size="large" placeholder={Resources.mobile} />
				</Form.Item>
				<Form.Item name="code" label={Resources.code} rules={Rules.required}>
					<InputCode placeholder={Resources.code} onResend={onResend} />
				</Form.Item>
				<Form.Item
					name="password"
					label={Resources.password}
					rules={Rules.required.concat(Rules.password)}
				>
					<Input.Password type="password" />
				</Form.Item>
				<Form.Item
					name="passwordConfirmation"
					label={`${Resources.repeat} ${Resources.password}`}
					rules={Rules.required.concat([{ validator: handleCompare }])}
				>
					<Input.Password type="password" />
				</Form.Item>
				<Form.Item style={{ paddingTop: 16 }}>
					<Button
						type="primary"
						size="large"
						loading={submitting}
						onClick={handleSubmit}
						block
					>
						{Resources.enter}
					</Button>
				</Form.Item>
			</Form>
		</div>
	);
};

export default Component;
